// src/pages/laboratory/Promotions.jsx - Updated with correct workflow
import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import {
  Tag,
  Search,
  Eye,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  Calendar,
  DollarSign,
  Percent,
  QrCode,
  Info,
  BarChart3,
  CheckSquare,
  ExternalLink,
  Copy,
  Download,
  StopCircle,
  TrendingUp,
  Users
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';

// Import your existing components

// Import API functions
import {
  GET_LABORATORY_BY_USER,
  GET_PROMOTIONS_BY_LABORATORY,
  UPDATE_PROMOTION,
  GET_PROMOTION_LABELS,
  GET_LABELS_STATISTICS,
  GET_LABELS_COUNT_BY_PROMOTION,
  GET_DRAFT_LABELS_COUNT_BY_PROMOTION,
  CONFIRM_LABELS
} from '../../api/laboratory-api';
import { authApi } from '../../api/auth-api';
import { getProductImageUrl } from '../../utils/imageUrl';

const PromotionsPage = () => {
  // State management
  const client = useApolloClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [modalActiveTab, setModalActiveTab] = useState('general');
  const [qrSearchTerm, setQrSearchTerm] = useState('');
  const [qrStatusFilter, setQrStatusFilter] = useState('all');
  const [promotionQRCounts, setPromotionQRCounts] = useState({});
  const [promotionDraftCounts, setPromotionDraftCounts] = useState({});
  const [promotionStats, setPromotionStats] = useState({});

  // Get current user data
  const userData = authApi.getUserData();
  const userId = userData?.id;

  // Fetch laboratory data for current user
  const { 
    data: laboratoryData, 
    loading: laboratoryLoading,
    error: laboratoryError 
  } = useQuery(GET_LABORATORY_BY_USER, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'cache-and-network'
  });

  const laboratory = laboratoryData?.laboratoryByUserId;

  // Fetch promotions for the laboratory
  const {
    data: promotionsData,
    loading: promotionsLoading,
    error: promotionsError,
    refetch: refetchPromotions
  } = useQuery(GET_PROMOTIONS_BY_LABORATORY, {
    variables: { laboratoryId: laboratory?.id || '' },
    skip: !laboratory?.id,
    fetchPolicy: 'cache-and-network'
  });

  // Query for promotion labels (for QR codes tab)
  const { 
    data: labelsData, 
    loading: labelsLoading, 
    refetch: refetchLabels 
  } = useQuery(GET_PROMOTION_LABELS, {
    variables: { promotionId: selectedPromotion?.id || '' },
    skip: !selectedPromotion?.id || modalActiveTab !== 'qrcodes',
    fetchPolicy: 'cache-and-network'
  });

  // Query for labels statistics
  const { 
    data: statsData, 
    loading: statsLoading 
  } = useQuery(GET_LABELS_STATISTICS, {
    variables: { promotionId: selectedPromotion?.id || '' },
    skip: !selectedPromotion?.id || (modalActiveTab !== 'qrcodes' && modalActiveTab !== 'stats'),
    fetchPolicy: 'cache-and-network'
  });

  const fetchQRCount = async (promotionId) => {
    try {
      const { data } = await client.query({
        query: GET_LABELS_COUNT_BY_PROMOTION,
        variables: { promotionId },
        fetchPolicy: 'network-only'
      });
      const count = data?.labelsCountByPromotion ?? 0;
      setPromotionQRCounts(prev => ({ ...prev, [promotionId]: count }));
      return count;
    } catch (error) {
      console.error('Error fetching QR count:', error);
      return 0;
    }
  };

  const fetchDraftCount = async (promotionId) => {
    try {
      const { data } = await client.query({
        query: GET_DRAFT_LABELS_COUNT_BY_PROMOTION,
        variables: { promotionId },
        fetchPolicy: 'network-only'
      });
      const count = data?.draftLabelsCountByPromotion || 0;
      setPromotionDraftCounts(prev => ({ ...prev, [promotionId]: count }));
      return count;
    } catch (error) {
      console.error('Error fetching draft count:', error);
      return 0;
    }
  };

  const fetchPromotionStats = async (promotionId) => {
    try {
      const { data } = await client.query({
        query: GET_LABELS_STATISTICS,
        variables: { promotionId },
        fetchPolicy: 'cache-first'
      });
      const s = data?.labelsStatistics;
      if (s) setPromotionStats(prev => ({ ...prev, [promotionId]: s }));
    } catch (error) {
      console.error('Error fetching promotion stats:', error);
    }
  };

  // Mutation to confirm draft QR labels (EDITED → NOT_SCANNED)
  const [confirmLabels, { loading: confirmingLabels }] = useMutation(CONFIRM_LABELS, {
    onCompleted: (data) => {
      if (data.confirmLabels?.success) {
        toast.success(data.confirmLabels.message);
        // Refresh draft counts for all IN_PROGRESS promotions
        promotions.filter(p => p.status === 'IN_PROGRESS').forEach(p => fetchDraftCount(p.id));
        refetchLabels();
      } else {
        toast.error(data.confirmLabels?.message || 'Erreur lors de la confirmation');
      }
    },
    onError: (error) => {
      toast.error(`Erreur: ${error.message}`);
    }
  });

  // Mutation for confirming QR code placement (DRAFT -> IN_PROGRESS)
  const [updatePromotion, { loading: updateLoading }] = useMutation(UPDATE_PROMOTION, {
    onCompleted: (data) => {
      if (data.updatePromotion) {
        toast.success('Statut de la promotion mis à jour avec succès');
        refetchPromotions();
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la mise à jour: ${error.message}`);
    }
  });

  // Get promotions array
  const promotions = promotionsData?.promotionsByLaboratory || [];

  // Track which promotion IDs we've already fetched this session to avoid duplicate network calls
  const fetchedQRIds = useRef(new Set());

  useEffect(() => {
    if (promotions.length === 0) return;
    promotions.forEach(promotion => {
      if (promotion.status === 'DRAFT' && !fetchedQRIds.current.has(promotion.id)) {
        fetchedQRIds.current.add(promotion.id);
        fetchQRCount(promotion.id);
      }
      if (promotion.status === 'IN_PROGRESS') {
        fetchDraftCount(promotion.id);
        fetchPromotionStats(promotion.id);
      }
      if (promotion.status === 'CLOSED') {
        fetchPromotionStats(promotion.id);
      }
    });
  }, [promotions]);

  const isPromotionActive = (promotion) => {
    const now = new Date();
    const startDate = new Date(promotion.startDate);
    const endDate = new Date(promotion.endDate);
    return now >= startDate && now <= endDate && promotion.status === 'IN_PROGRESS';
  };

  // Build flat list of ALL (product × promotion) pairs — no deduplication
  const promoStatusPriority = (promo) => {
    if (isPromotionActive(promo)) return 0;
    if (promo.status === 'DRAFT') return 1;
    if (promo.status === 'IN_PROGRESS') return 2;
    return 3; // CLOSED
  };

  const allProductPromos = (() => {
    const pairs = [];
    promotions.forEach(promo => {
      (promo.products || []).forEach(product => {
        pairs.push({ ...product, promo });
      });
    });
    return pairs;
  })();

  const filteredProducts = allProductPromos
    .filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || item.promo.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      // First sort by product name
      const nameCompare = a.name.localeCompare(b.name);
      if (nameCompare !== 0) return nameCompare;
      // Then by promotion priority within the same product
      return promoStatusPriority(a.promo) - promoStatusPriority(b.promo);
    });

  // Helper functions
  const getStatusDisplay = (status) => {
    switch(status) {
      case 'IN_PROGRESS':
        return { 
          text: 'En cours', 
          className: 'bg-green-100 text-green-800', 
          icon: CheckCircle,
          description: 'QR codes collés et promotion active'
        };
      case 'DRAFT':
        return { 
          text: 'En attente', 
          className: 'bg-yellow-100 text-yellow-800', 
          icon: Clock,
          description: 'QR codes générés, en attente de collage'
        };
      case 'CLOSED':
        return { 
          text: 'Terminée', 
          className: 'bg-red-100 text-red-800', 
          icon: XCircle,
          description: 'Promotion fermée'
        };
      default:
        return { 
          text: status, 
          className: 'bg-gray-100 text-gray-800', 
          icon: Info,
          description: 'Statut inconnu'
        };
    }
  };

  const getLabelStatusDisplay = (status) => {
    switch(status) {
      case 'SCANNED':
        return { text: 'Scanné', className: 'bg-green-100 text-green-800', icon: CheckSquare };
      case 'NOT_SCANNED':
        return { text: 'Non scanné', className: 'bg-yellow-100 text-yellow-800', icon: Eye };
      case 'EDITED':
        return { text: 'Brouillon', className: 'bg-orange-100 text-orange-800', icon: Clock };
      default:
        return { text: status, className: 'bg-gray-100 text-gray-800', icon: XCircle };
    }
  };

  const canConfirmQRPlacement = (promotion) => {
    return promotion.status === 'DRAFT' && 
           promotion.quantity > 0 && 
           (promotionQRCounts[promotion.id] || 0) > 0;
  };

  // Event handlers
  const handleViewPromotion = (promotion) => {
    setSelectedPromotion(promotion);
    setModalActiveTab('general');
    setIsViewModalOpen(true);
  };

  const handleConfirmQRPlacement = (promotion) => {
    if (!canConfirmQRPlacement(promotion)) {
      toast.warning('Cette promotion ne peut pas être activée');
      return;
    }
    if (!window.confirm('Confirmez-vous que les QR codes ont bien été collés sur les produits ? La promotion sera activée immédiatement.')) return;

    updatePromotion({
      variables: {
        updatePromotionInput: {
          id: promotion.id,
          status: 'IN_PROGRESS'
        }
      }
    });
  };

  const handleStopPromotion = (promotion) => {
    updatePromotion({
      variables: {
        updatePromotionInput: {
          id: promotion.id,
          status: 'CLOSED'
        }
      }
    });
  };

  // Render QR Codes tab content
  const renderQRCodesTab = () => {
    const labels = labelsData?.labelsByPromotion || [];
    const draftCount   = labels.filter(l => l.status === 'EDITED').length;
    const activeCount  = labels.filter(l => l.status === 'NOT_SCANNED').length;
    const scannedCount = labels.filter(l => l.status === 'SCANNED').length;
    const statistics = statsData?.labelsStatistics || {
      totalGenerated: 0,
      totalUsed: 0,
      totalUnused: 0,
      usageRate: 0
    };

    const filteredLabels = labels.filter(label => {
      const matchesSearch = label.code.toLowerCase().includes(qrSearchTerm.toLowerCase()) ||
                           (label.user && `${label.user.firstName} ${label.user.lastName}`.toLowerCase().includes(qrSearchTerm.toLowerCase()));
      const matchesStatus = qrStatusFilter === 'all' ||
                           (qrStatusFilter === 'used' && label.isUsed) ||
                           (qrStatusFilter === 'unused' && !label.isUsed) ||
                           (qrStatusFilter === label.status);
      return matchesSearch && matchesStatus;
    });

    return (
      <div className="space-y-6">
        {/* Status Message */}
        {selectedPromotion && selectedPromotion.status === 'DRAFT' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-yellow-600 mr-3" />
              <div>
                <h4 className="text-yellow-800 font-medium">QR codes générés par l'administrateur</h4>
                <p className="text-yellow-700 text-sm">
                  Les QR codes ont été générés et imprimés. Collez-les sur vos produits puis confirmez pour activer la promotion.
                </p>
              </div>
            </div>
          </div>
        )}


        {/* Statistics — 4 cards clickable as filters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Total */}
          <div
            className="bg-blue-50 p-4 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
            onClick={() => setQrStatusFilter('all')}
          >
            <div className="flex items-center gap-3">
              <QrCode className="h-7 w-7 text-blue-600 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-blue-600">Total</p>
                <p className="text-2xl font-bold text-blue-800">{statistics.totalGenerated || labels.length}</p>
              </div>
            </div>
          </div>

          {/* Actifs */}
          <div
            className="bg-green-50 p-4 rounded-lg border border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
            onClick={() => setQrStatusFilter('NOT_SCANNED')}
          >
            <div className="flex items-center gap-3">
              <Eye className="h-7 w-7 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-green-600">Actifs</p>
                <p className="text-2xl font-bold text-green-800">{activeCount}</p>
              </div>
            </div>
          </div>

          {/* Brouillon */}
          <div
            className={`p-4 rounded-lg border cursor-pointer transition-colors ${draftCount > 0 ? 'bg-orange-50 border-orange-300 hover:bg-orange-100' : 'bg-gray-50 border-gray-200'}`}
            onClick={() => setQrStatusFilter('EDITED')}
          >
            <div className="flex items-center gap-3">
              <Clock className={`h-7 w-7 flex-shrink-0 ${draftCount > 0 ? 'text-orange-500' : 'text-gray-400'}`} />
              <div>
                <p className={`text-xs font-medium ${draftCount > 0 ? 'text-orange-600' : 'text-gray-500'}`}>Brouillon</p>
                <p className={`text-2xl font-bold ${draftCount > 0 ? 'text-orange-700' : 'text-gray-400'}`}>{draftCount}</p>
              </div>
            </div>
          </div>

          {/* Scannés */}
          <div
            className="bg-purple-50 p-4 rounded-lg border border-purple-200 cursor-pointer hover:bg-purple-100 transition-colors"
            onClick={() => setQrStatusFilter('SCANNED')}
          >
            <div className="flex items-center gap-3">
              <CheckSquare className="h-7 w-7 text-purple-600 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-purple-600">Scannés</p>
                <p className="text-2xl font-bold text-purple-800">{scannedCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-3">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Rechercher un code QR..."
              value={qrSearchTerm}
              onChange={(e) => setQrSearchTerm(e.target.value)}
            />
          </div>

          <div className="relative w-full sm:w-48">
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={qrStatusFilter}
              onChange={(e) => setQrStatusFilter(e.target.value)}
            >
              <option value="all">Tous les codes</option>
              <option value="used">Utilisés</option>
              <option value="unused">Non utilisés</option>
              <option value="SCANNED">Scannés</option>
              <option value="NOT_SCANNED">Non scannés</option>
              <option value="EDITED">En édition</option>
            </select>
          </div>
        </div>

        {/* QR Codes List */}
        {!labelsLoading && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {filteredLabels.length === 0 ? (
              <div className="text-center py-8">
                <QrCode size={40} className="mx-auto text-gray-400 mb-2" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {statistics.totalGenerated === 0 ? 'Aucun code QR généré' : 'Aucun code QR trouvé'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {statistics.totalGenerated === 0 
                    ? "Les codes QR seront générés par l'administrateur." 
                    : "Aucun code QR ne correspond à vos critères."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Code QR
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Utilisateur
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date d'utilisation
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date de création
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pharmacie
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLabels.map((label) => {
                      const statusDisplay = getLabelStatusDisplay(label.status);
                      const StatusIcon = statusDisplay.icon;
                      const isDraft = label.status === 'EDITED';

                      return (
                        <tr key={label.id} className={isDraft ? 'bg-orange-50/60 hover:bg-orange-50' : 'hover:bg-gray-50'}>
                          <td className={`px-6 py-4 whitespace-nowrap ${isDraft ? 'border-l-2 border-orange-400' : ''}`}>
                            <div className="flex items-center">
                              <div className={`flex-shrink-0 h-10 w-10 rounded-md flex items-center justify-center mr-3 ${isDraft ? 'bg-orange-100' : 'bg-gray-100'}`}>
                                <QrCode className={`h-5 w-5 ${isDraft ? 'text-orange-500' : 'text-gray-600'}`} />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900 font-mono">
                                  {label.code}
                                </div>
                                <div className="text-xs text-gray-500">
                                  ID: {label.id.substring(0, 8)}...
                                </div>
                              </div>
                            </div>
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <StatusIcon className="h-4 w-4 mr-2 text-gray-400" />
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusDisplay.className}`}>
                                {statusDisplay.text}
                              </span>
                            </div>
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            {label.user ? (
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {label.user.firstName} {label.user.lastName}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {label.user.email}
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">Non utilisé</span>
                            )}
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {label.date ? (
                              <div>
                                <div>{format(new Date(label.date), 'dd/MM/yyyy', { locale: fr })}</div>
                                <div className="text-xs">{format(new Date(label.date), 'HH:mm', { locale: fr })}</div>
                              </div>
                            ) : (
                              <span>-</span>
                            )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {label.createdAt ? (
                              <div>
                                <div>{format(new Date(label.createdAt), 'dd/MM/yyyy', { locale: fr })}</div>
                                <div className="text-xs">{format(new Date(label.createdAt), 'HH:mm', { locale: fr })}</div>
                              </div>
                            ) : (
                              <span>-</span>
                            )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            {label.pharmacy ? (
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {label.pharmacy.namePharmacy}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {label.pharmacy.region?.nomRegion} - {label.pharmacy.ville?.nomVilleVillage}
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Loading and error states
  if (laboratoryLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-700 border-r-transparent"></div>
        <p className="ml-3 text-gray-600">Chargement de votre laboratoire...</p>
      </div>
    );
  }

  if (laboratoryError || !laboratory) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-red-800">Erreur</h3>
            <p className="text-red-700">
              {laboratoryError ? laboratoryError.message : 'Laboratoire non trouvé. Contactez l\'administrateur.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-green-700 to-emerald-600 text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Tag className="w-8 h-8 mr-3" />
            <div>
              <h1 className="text-2xl font-bold">Mes Promotions</h1>
              <p className="text-green-100">
                Visualisez et gérez vos promotions - {laboratory.nom}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm text-green-100">Total promotions</p>
              <p className="text-2xl font-bold">{promotions.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent focus:bg-white transition-colors"
            placeholder="Rechercher un produit..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'Tous', count: allProductPromos.length },
            { value: 'IN_PROGRESS', label: 'Actifs', count: allProductPromos.filter(p => p.promo.status === 'IN_PROGRESS').length },
            { value: 'DRAFT', label: 'En attente', count: allProductPromos.filter(p => p.promo.status === 'DRAFT').length },
            { value: 'CLOSED', label: 'Terminés', count: allProductPromos.filter(p => p.promo.status === 'CLOSED').length },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center space-x-1 ${
                statusFilter === opt.value
                  ? 'bg-green-700 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>{opt.label}</span>
              <span className={`${statusFilter === opt.value ? 'bg-green-600' : 'bg-gray-200 text-gray-500'} rounded-full px-1.5 py-0.5 text-xs`}>
                {opt.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Products with Promo — Card Grid */}
      {promotionsLoading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-700 border-r-transparent"></div>
          <p className="mt-3 text-sm text-gray-500">Chargement...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
          <Package size={40} className="mx-auto text-gray-300 mb-3" />
          <h3 className="text-sm font-semibold text-gray-700 mb-1">
            {searchTerm || statusFilter !== 'all' ? 'Aucun produit trouvé' : 'Aucun produit en promotion'}
          </h3>
          <p className="text-xs text-gray-400">
            {searchTerm || statusFilter !== 'all'
              ? 'Modifiez vos critères de recherche.'
              : "Les promotions et produits associés apparaîtront ici."}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          {/* Table header */}
          <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[600px]">
            <div className="col-span-3">Produit</div>
            <div className="col-span-2">Commission</div>
            <div className="col-span-2">Période</div>
            <div className="col-span-3">QR Codes</div>
            <div className="col-span-1">Statut</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {filteredProducts.map((item, idx) => {
            const isActive = isPromotionActive(item.promo);
            const statusDisplay = getStatusDisplay(item.promo.status);
            const canConfirm = canConfirmQRPlacement(item.promo);
            const isLast = idx === filteredProducts.length - 1;

            const draftCount = promotionDraftCounts[item.promo.id] || 0;
            const ps = promotionStats[item.promo.id];
            const totalGenerated = ps?.totalGenerated ?? promotionQRCounts[item.promo.id] ?? null;
            const totalScanned   = ps?.totalUsed ?? null;
            const usageRate      = ps?.usageRate ?? null;

            // Detect if same product as previous row (for grouping visual)
            const prevItem = idx > 0 ? filteredProducts[idx - 1] : null;
            const isSameProductAsPrev = prevItem && prevItem.id === item.id;

            return (
              <div
                key={`${item.id}-${item.promo.id}`}
                className={`sm:grid sm:grid-cols-12 flex flex-wrap gap-3 px-4 py-3.5 items-center cursor-pointer hover:bg-gray-50 transition-colors min-w-0 ${
                  !isLast ? 'border-b border-gray-100' : ''
                } ${isSameProductAsPrev ? 'bg-gray-50/50' : ''}`}
                onClick={() => handleViewPromotion(item.promo)}
              >
                {/* Product — show info only for first row of each product */}
                <div className="col-span-3 flex items-center gap-3 min-w-0">
                  {isSameProductAsPrev ? (
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <div className="w-1 h-4 rounded-full bg-gray-300" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {item.image
                        ? <img src={getProductImageUrl(item.image)} alt="" className="w-10 h-10 object-cover" />
                        : <Package size={16} className="text-green-400" />}
                    </div>
                  )}
                  <div className="min-w-0">
                    {!isSameProductAsPrev && (
                      <>
                        <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                        <p className="text-xs text-gray-400">{item.price} DH</p>
                      </>
                    )}
                    {isSameProductAsPrev && (
                      <p className="text-xs text-gray-400 truncate">{item.promo.name}</p>
                    )}
                  </div>
                </div>

                {/* Commission */}
                <div className="col-span-2">
                  {item.promo.commissionAmount > 0 && (
                    <span className="text-sm font-bold text-green-700">+{item.promo.commissionAmount} DH</span>
                  )}
                  {item.promo.commissionPercentage > 0 && (
                    <span className="text-xs text-gray-500 block">{item.promo.commissionPercentage}% Mazid</span>
                  )}
                </div>

                {/* Période */}
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">
                    {item.promo.startDate ? new Date(item.promo.startDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—'}
                  </p>
                  <p className="text-xs text-gray-400">
                    → {item.promo.endDate ? new Date(item.promo.endDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                  </p>
                </div>

                {/* QR Codes stats */}
                <div className="col-span-3 flex items-center gap-2">
                  {totalGenerated === null ? (
                    <span className="text-xs text-gray-400">—</span>
                  ) : (
                    <>
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <QrCode size={11} className="text-gray-400" />
                        <span className="font-semibold">{totalGenerated}</span>
                      </div>
                      {totalScanned !== null && (
                        <>
                          <span className="text-gray-300">·</span>
                          <div className="flex items-center gap-1 text-xs text-green-700">
                            <CheckCircle size={11} />
                            <span className="font-semibold">{totalScanned}</span>
                          </div>
                          <span className="text-gray-300">·</span>
                          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                            usageRate >= 75 ? 'bg-green-100 text-green-700' :
                            usageRate >= 40 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {usageRate !== null ? `${Math.round(usageRate)}%` : '—'}
                          </span>
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Statut */}
                <div className="col-span-1">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${
                    isActive ? 'bg-green-100 text-green-700' : statusDisplay.className
                  }`}>
                    {isActive && <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />}
                    {isActive ? 'Active' : statusDisplay.text}
                  </span>
                </div>

                {/* Actions */}
                <div className="col-span-1 flex flex-col items-end gap-1.5" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    {canConfirm && (
                      <button
                        onClick={() => handleConfirmQRPlacement(item.promo)}
                        disabled={updateLoading}
                        className="flex items-center gap-1 px-2 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition-colors"
                        title="Confirmer le collage"
                      >
                        <CheckCircle size={11} />
                        Confirmer le collage
                      </button>
                    )}
                  </div>
                  {draftCount > 0 && (
                    <button
                      onClick={() => {
                        if (!window.confirm(`Confirmez-vous que les ${draftCount} QR code${draftCount > 1 ? 's' : ''} ont bien été collés sur les produits ?`)) return;
                        confirmLabels({ variables: { promotionId: item.promo.id } });
                      }}
                      disabled={confirmingLabels}
                      className="flex items-center gap-1 px-2 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition-colors"
                      title={`${draftCount} brouillon${draftCount > 1 ? 's' : ''} — Confirmer le collage`}
                    >
                      {confirmingLabels
                        ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : <Clock size={11} />}
                      Confirmer le collage ({draftCount})
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Promotion View Modal */}
      {isViewModalOpen && selectedPromotion && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsViewModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">

            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                  selectedPromotion.status === 'IN_PROGRESS' ? 'bg-green-500 animate-pulse' :
                  selectedPromotion.status === 'DRAFT' ? 'bg-amber-400' : 'bg-gray-400'
                }`} />
                <div className="min-w-0">
                  <h2 className="text-base font-bold text-gray-900 truncate">{selectedPromotion.name}</h2>
                  <p className="text-xs text-gray-400">{getStatusDisplay(selectedPromotion.status).text} · {getStatusDisplay(selectedPromotion.status).description}</p>
                </div>
              </div>
              <button onClick={() => setIsViewModalOpen(false)}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition flex-shrink-0">
                <XCircle size={18} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-6 pt-3 border-b border-gray-100">
              {[
                { id: 'general', label: 'Général',    Icon: Info },
                { id: 'qrcodes', label: 'Codes QR',   Icon: QrCode },
                { id: 'stats',   label: 'Statistiques', Icon: BarChart3 },
              ].map(({ id, label, Icon }) => (
                <button key={id} onClick={() => setModalActiveTab(id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                    modalActiveTab === id
                      ? 'border-green-600 text-green-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}>
                  <Icon size={14} />{label}
                </button>
              ))}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">

              {/* ── Général tab ──────────────────────────────────────────── */}
              {modalActiveTab === 'general' && (
                <>
                  {/* Confirmation de collage banner */}
                  {selectedPromotion.status === 'DRAFT' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <Clock size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-amber-800 text-sm">QR codes prêts à coller</p>
                          <p className="text-xs text-amber-700 mt-0.5">
                            L'administrateur a généré les QR codes. Collez-les sur vos produits puis confirmez.
                          </p>
                        </div>
                      </div>
                      {canConfirmQRPlacement(selectedPromotion) && (
                        <button
                          onClick={() => { handleConfirmQRPlacement(selectedPromotion); setIsViewModalOpen(false); }}
                          disabled={updateLoading}
                          className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50 flex-shrink-0 transition"
                        >
                          {updateLoading
                            ? <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : <CheckCircle size={14} />}
                          Confirmer le collage
                        </button>
                      )}
                    </div>
                  )}

                  {/* Info grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      { label: 'Commission vendeur', value: `${selectedPromotion.commissionAmount} DH`, accent: 'green' },
                      { label: 'Commission Mazid',   value: `${selectedPromotion.commissionPercentage}%`, accent: 'blue' },
                      { label: 'QR codes',           value: selectedPromotion.quantity ?? '—', accent: 'violet' },
                    ].map(c => (
                      <div key={c.label} className={`bg-${c.accent}-50 border border-${c.accent}-100 rounded-2xl p-4`}>
                        <p className={`text-xs text-${c.accent}-600 mb-1`}>{c.label}</p>
                        <p className={`text-xl font-bold text-${c.accent}-700`}>{c.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Dates + type */}
                  <div className="bg-white border border-gray-100 rounded-2xl p-4 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Date de début</p>
                      <p className="text-sm font-medium text-gray-800">
                        {format(new Date(selectedPromotion.startDate), 'dd MMMM yyyy', { locale: fr })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Date de fin</p>
                      <p className="text-sm font-medium text-gray-800">
                        {format(new Date(selectedPromotion.endDate), 'dd MMMM yyyy', { locale: fr })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Type de commission</p>
                      <p className="text-sm font-medium text-gray-800">
                        {selectedPromotion.commissionType === 'PERCENTAGE' ? 'Pourcentage' : 'Montant fixe'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Statut</p>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusDisplay(selectedPromotion.status).className}`}>
                        {getStatusDisplay(selectedPromotion.status).text}
                      </span>
                    </div>
                  </div>

                  {/* Products */}
                  <div className="bg-white border border-gray-100 rounded-2xl p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-3">
                      Produits associés ({selectedPromotion.products?.length ?? 0})
                    </p>
                    {selectedPromotion.products?.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedPromotion.products.map(prod => (
                          <div key={prod.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-xl">
                            <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                              {prod.image
                                ? <img src={getProductImageUrl(prod.image)} alt="" className="w-9 h-9 object-cover" />
                                : <Package size={14} className="text-gray-400" />}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{prod.name}</p>
                              {prod.price && <p className="text-xs text-gray-400">{prod.price.toFixed(2)} DH</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 text-center py-4">Aucun produit associé</p>
                    )}
                  </div>


                  <p className="text-xs text-gray-400 text-center">
                    Seul l'administrateur peut générer et imprimer les QR codes.
                  </p>
                </>
              )}

              {/* ── QR Codes tab ─────────────────────────────────────────── */}
              {modalActiveTab === 'qrcodes' && renderQRCodesTab()}

              {/* ── Stats tab ────────────────────────────────────────────── */}
              {modalActiveTab === 'stats' && (() => {
                if (statsLoading) return (
                  <div className="text-center py-10">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent" />
                    <p className="mt-2 text-gray-500">Chargement des statistiques…</p>
                  </div>
                );

                const stats = statsData?.labelsStatistics;
                if (!stats) return (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <BarChart3 size={48} className="mb-3 text-gray-300" />
                    <p className="font-medium text-gray-600">Aucune donnée disponible</p>
                    <p className="text-sm mt-1">Les statistiques apparaîtront une fois les QR codes scannés.</p>
                  </div>
                );

                const dailyScans = stats.dailyScans || [];
                const promoStart = selectedPromotion?.startDate ? new Date(selectedPromotion.startDate).toISOString().slice(0, 10) : null;
                const promoEnd   = selectedPromotion?.endDate   ? new Date(selectedPromotion.endDate).toISOString().slice(0, 10)   : null;
                const rangeScans = dailyScans.filter(d => {
                  if (promoStart && d.date < promoStart) return false;
                  if (promoEnd   && d.date > promoEnd)   return false;
                  return true;
                });
                const maxDaily = rangeScans.reduce((m, d) => Math.max(m, d.count), 0) || 1;

                return (
                  <div className="space-y-5">
                    {/* KPI cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
                        <QrCode className="h-8 w-8 text-blue-600 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-blue-600">QR générés</p>
                          <p className="text-2xl font-bold text-blue-800">{stats.totalGenerated}</p>
                        </div>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                        <CheckCircle className="h-8 w-8 text-green-600 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-green-600">Utilisés</p>
                          <p className="text-2xl font-bold text-green-800">{stats.totalUsed}</p>
                        </div>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3">
                        <XCircle className="h-8 w-8 text-yellow-600 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-yellow-600">Non utilisés</p>
                          <p className="text-2xl font-bold text-yellow-800">{stats.totalUnused}</p>
                        </div>
                      </div>
                      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center gap-3">
                        <BarChart3 className="h-8 w-8 text-purple-600 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-purple-600">Taux d'usage</p>
                          <p className="text-2xl font-bold text-purple-800">{(stats.usageRate ?? 0).toFixed(1)}%</p>
                        </div>
                      </div>
                    </div>

                    {/* Time-based stats */}
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: "Aujourd'hui",   value: stats.scannedToday || 0 },
                        { label: 'Cette semaine', value: stats.scannedThisWeek || 0 },
                        { label: 'Ce mois',       value: stats.scannedThisMonth || 0 },
                      ].map(({ label, value }) => (
                        <div key={label} className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1">{label}</p>
                          <p className="text-2xl font-bold text-gray-800">{value}</p>
                          <p className="text-xs text-gray-400">scans</p>
                        </div>
                      ))}
                    </div>

                    {/* Scan evolution chart */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                        <TrendingUp size={16} className="text-green-500" />
                        Évolution des scans
                      </h4>
                      {selectedPromotion && (
                        <p className="text-xs text-gray-400 mb-3">
                          {format(new Date(selectedPromotion.startDate), 'dd/MM/yyyy', { locale: fr })}
                          {' → '}
                          {format(new Date(selectedPromotion.endDate), 'dd/MM/yyyy', { locale: fr })}
                        </p>
                      )}
                      {rangeScans.length === 0 ? (
                        <div className="flex items-center justify-center h-24 text-gray-400 text-sm">
                          Aucune donnée de scan sur cette période
                        </div>
                      ) : (
                        <>
                          <div className="flex items-end gap-0.5 h-32">
                            {rangeScans.map((d, i) => {
                              const pct = Math.round((d.count / maxDaily) * 100);
                              return (
                                <div key={i} className="flex flex-col items-center flex-1 min-w-[4px]"
                                  title={`${d.date}: ${d.count} scan${d.count !== 1 ? 's' : ''}`}>
                                  <div className="w-full bg-green-500 hover:bg-green-600 rounded-t transition-colors"
                                    style={{ height: `${pct}%`, minHeight: d.count > 0 ? 3 : 0 }} />
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex justify-between text-xs text-gray-400 mt-1">
                            <span>{rangeScans[0]?.date || ''}</span>
                            <span>{rangeScans[rangeScans.length - 1]?.date || ''}</span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Top pharmacies */}
                    {stats.topPharmacies?.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <Users size={16} className="text-green-500" />
                          Top pharmacies
                        </h4>
                        <div className="space-y-2">
                          {stats.topPharmacies.map((item, i) => {
                            const pct = Math.round((item.count / (stats.topPharmacies[0]?.count || 1)) * 100);
                            return (
                              <div key={item.pharmacy.id} className="flex items-center gap-3">
                                <span className="text-xs font-bold text-gray-400 w-4 text-right">{i + 1}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800 font-medium truncate">{item.pharmacy.namePharmacy}</p>
                                </div>
                                <div className="w-24 bg-gray-100 rounded-full h-2 flex-shrink-0">
                                  <div className="bg-green-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-sm font-semibold text-gray-700 w-14 text-right flex-shrink-0">
                                  {item.count} scan{item.count !== 1 ? 's' : ''}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionsPage;